import { supabase } from "@/integrations/supabase/client";

export type MediaFilteredRow = {
  name: string;
  full_path: string;
  is_folder: boolean;
  size: number | string | null;
  content_type: string | null;
  created_at: string | null;
  updated_at: string | null;
};

export type MediaFilterRequest = {
  prefix?: string;
  search?: string;
  type?: "all" | "folder" | "image" | "other";
  size?: "all" | "lt100kb" | "100kb-1mb" | "1mb-5mb" | "gt5mb";
  updated?: "all" | "today" | "7d" | "30d" | "older";
  sortBy?: "name" | "size" | "updated";
  sortDir?: "asc" | "desc";
  limit?: number;
};

function getSupabaseRestConfig() {
  const env = import.meta.env;
  const url = env.VITE_SUPABASE_URL;
  const key = env.VITE_SUPABASE_PUBLISHABLE_KEY || env.VITE_SUPABASE_ANON_KEY;

  if (!url || !key) {
    throw new Error("Missing Supabase media configuration.");
  }

  return { url, key };
}

async function readErrorMessage(response: Response) {
  try {
    const body = await response.json();
    return body?.message || body?.error || response.statusText;
  } catch {
    return response.statusText;
  }
}

export async function listMediaFiltered(args: MediaFilterRequest): Promise<MediaFilteredRow[]> {
  const { url, key } = getSupabaseRestConfig();
  const { data: sessionData, error: sessionError } = await supabase.auth.getSession();

  if (sessionError) throw sessionError;
  if (!sessionData.session?.access_token) {
    throw new Error("Sign in as staff or admin to manage media.");
  }

  const response = await fetch(`${url}/rest/v1/rpc/list_media_filtered`, {
    method: "POST",
    headers: {
      apikey: key,
      Authorization: `Bearer ${sessionData.session.access_token}`,
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({
      p_prefix: args.prefix ?? "",
      p_search: args.search ?? "",
      p_type: args.type ?? "all",
      p_size: args.size ?? "all",
      p_updated: args.updated ?? "all",
      p_sort_by: args.sortBy ?? "name",
      p_sort_dir: args.sortDir ?? "asc",
      p_limit: args.limit ?? 1000,
    }),
  });

  if (!response.ok) {
    const message = await readErrorMessage(response);
    if (message.toLowerCase().includes("not authorised")) {
      throw new Error("Only staff and admin users can manage media.");
    }
    throw new Error(message || "Media request failed.");
  }

  return (await response.json()) as MediaFilteredRow[];
}
